/**
 * Send feedback from inside MC Planner, the pure part: the form auth.lab43.dev hands out, the proof-of-work for posting
 * without an account, the request, and what its answers mean (lab43-auth docs/API.md, "Feedback from inside the
 * apps"). The dialog is ui/Feedback.tsx; collecting the version and system is lib/feedback.ts.
 *
 * No imports, so it runs in Node for the tests (apps/web/scripts/feedback.test.ts).
 *
 * Lab43's rules: nothing is sent until the person presses Send, one POST per Send (no retries, no queue), the person
 * sees everything that goes with it first, and the only facts about MC Planner are its version, the system and the
 * screen it was sent from. Never anything about the person's plans, worlds, schematics or files.
 */

export const FEEDBACK_API = "https://auth.lab43.dev";
export const FEEDBACK_APP = "mcplanner";
/** MC Planner's public board on the Lab43 feedback forum (lab43-auth docs/FEEDBACK.md) */
export const FEEDBACK_URL = `${FEEDBACK_API}/feedback/${FEEDBACK_APP}`;
export const FEEDBACK_FORM_URL = `${FEEDBACK_API}/api/v1/feedback/form?app=${FEEDBACK_APP}`;
export const FEEDBACK_POSTS_URL = `${FEEDBACK_API}/api/v1/feedback/posts`;

/** a form's token works from 3 seconds to 2 hours after it was made */
export const FORM_MIN_AGE_MS = 3000;
export const FORM_MAX_AGE_MS = 2 * 60 * 60 * 1000;

export interface FeedbackLimits { titleMin: number; titleMax: number; bodyMax: number }
export const DEFAULT_LIMITS: FeedbackLimits = { titleMin: 4, titleMax: 120, bodyMax: 4000 };
/** each context value: plain text of at most 60 characters */
const CONTEXT_MAX = 60;
const MAX_MESSAGE = 300;
/** the proof-of-work never needs more than 32 bits (the account site's limit) */
const MAX_BITS = 32;
const SALT = /^[A-Za-z0-9_-]{16,64}$/;

export interface FeedbackForm {
  /** posting without an account is on right now */
  anonymous: boolean;
  /** all: every post waits for Lab43's approval · anonymous: only posts without an account wait */
  postApproval: "all" | "anonymous";
  token: string;
  /** the proof-of-work's input */
  salt: string;
  /** leading zero bits the proof-of-work needs */
  bits: number;
  limits: FeedbackLimits;
  /** the public board (auth.lab43.dev/feedback/mcplanner) */
  board: string;
}

/** What the screen says goes with the post, and exactly what is sent as `context`. */
export interface FeedbackContext { appVersion: string; platform: string; page?: string }

// ---------------------------------------------------------------- text

/** control characters out, runs of white space as one space */
const plain = (s: string) => s.replace(/[\u0000-\u001f\u007f-\u009f]+/g, " ").replace(/\s+/g, " ").trim();
const chars = (s: string) => Array.from(s).length;
const cutChars = (s: string, n: number) => (chars(s) <= n ? s : Array.from(s).slice(0, n).join(""));

/** Only links on the account site's boards are opened from the dialog. */
function boardLink(url: unknown): string | null {
  if (typeof url !== "string" || url.length > 300) return null;
  return url.startsWith(`${FEEDBACK_API}/feedback/`) && !/[\s"<>\\]/.test(url) ? url : null;
}

// ---------------------------------------------------------------- the form

/** GET /api/v1/feedback/form's answer, checked. Null when it isn't a usable form. */
export function parseFeedbackForm(data: unknown): FeedbackForm | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;
  if (typeof d.token !== "string" || !d.token || d.token.length > 300) return null;
  if (typeof d.salt !== "string" || !SALT.test(d.salt)) return null;
  if (typeof d.bits !== "number" || !Number.isInteger(d.bits) || d.bits < 0 || d.bits > MAX_BITS) return null;
  const l = (d.limits && typeof d.limits === "object" ? d.limits : {}) as Record<string, unknown>;
  const n = (v: unknown, fallback: number, max: number) => (typeof v === "number" && Number.isInteger(v) && v > 0 && v <= max ? v : fallback);
  const limits = { titleMin: n(l.titleMin, DEFAULT_LIMITS.titleMin, 100), titleMax: n(l.titleMax, DEFAULT_LIMITS.titleMax, 1000), bodyMax: n(l.bodyMax, DEFAULT_LIMITS.bodyMax, 20_000) };
  if (limits.titleMin > limits.titleMax) return null;
  return {
    anonymous: d.anonymous === true,
    postApproval: d.postApproval === "anonymous" ? "anonymous" : "all",
    token: d.token,
    salt: d.salt,
    bits: d.bits,
    limits,
    board: boardLink(d.board) ?? FEEDBACK_URL,
  };
}

/** Does a post sent this way wait for Lab43's approval? */
export function waitsForApproval(form: Pick<FeedbackForm, "postApproval">, signedIn: boolean): boolean {
  return form.postApproval === "all" || !signedIn;
}

/** What's wrong with the title and details as typed, or null when they can be sent. */
export function checkDraft(title: string, body: string, limits: FeedbackLimits = DEFAULT_LIMITS): string | null {
  const t = chars(title.trim());
  if (t < limits.titleMin || t > limits.titleMax) return `Give it a title of ${limits.titleMin} to ${limits.titleMax} characters.`;
  if (chars(body.trim()) > limits.bodyMax) return `The details can be ${limits.bodyMax.toLocaleString("en-GB")} characters at most.`;
  return null;
}

// ---------------------------------------------------------------- proof-of-work

/** SHA-256 of the text's UTF-8 bytes */
export type Sha256 = (data: string) => Promise<Uint8Array>;

/** SHA-256 with Web Crypto (the browser, the desktop app's webview, Node 22) */
export const webSha256: Sha256 = async (data) => new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(data)));

/** How many zero bits the bytes start with. */
export function leadingZeroBits(bytes: Uint8Array): number {
  let n = 0;
  for (const b of bytes) {
    if (b === 0) { n += 8; continue; }
    return n + Math.clz32(b) - 24;
  }
  return n;
}

/** Does SHA-256(salt + ":" + nonce) start with `bits` zero bits? (The account site's check.) */
export async function checkNonce(salt: string, nonce: string, bits: number, sha256: Sha256 = webSha256): Promise<boolean> {
  return /^\d{1,15}$/.test(nonce) && leadingZeroBits(await sha256(`${salt}:${nonce}`)) >= bits;
}

/** a pause between batches, so the page can draw and take input */
const breathe = () => new Promise<void>((r) => setTimeout(r, 0));

/**
 * Finds the proof-of-work for posting without an account: the first nonce, counting up from 0, such that
 * SHA-256(salt + ":" + nonce) starts with `bits` zero bits (16: about 65,000 hashes). Hashes `batch` at a time with Web
 * Crypto and pauses between batches, so the page stays responsive. Null when `signal` aborts or after `maxTries`.
 */
export async function solveProofOfWork(
  salt: string,
  bits: number,
  opts: { sha256?: Sha256; batch?: number; pause?: () => Promise<void>; signal?: AbortSignal; maxTries?: number } = {},
): Promise<string | null> {
  const sha256 = opts.sha256 ?? webSha256;
  const batch = Math.max(1, Math.floor(opts.batch ?? 256));
  const pause = opts.pause ?? breathe;
  const maxTries = Math.min(opts.maxTries ?? 2 ** 26, 1e15);
  for (let start = 0; start < maxTries; start += batch) {
    if (opts.signal?.aborted) return null;
    const count = Math.min(batch, maxTries - start);
    const hashes = await Promise.all(Array.from({ length: count }, (_, i) => sha256(`${salt}:${start + i}`)));
    for (let i = 0; i < count; i++) if (leadingZeroBits(hashes[i]) >= bits) return String(start + i);
    await pause();
  }
  return null;
}

// ---------------------------------------------------------------- what goes with it

/** Where the person was when they opened the dialog: the demo island or a world, and an open panel. Never a name. */
export function pageLabel(p: { demo?: boolean; panel?: string | null; board?: boolean }): string {
  const parts = [p.demo ? "Demo island" : "Planner"];
  if (p.panel === "schematics") parts.push("Schematics");
  else if (p.panel) parts.push("Layouts");
  if (p.board) parts.push("Pinboard");
  return parts.join(" · ");
}

/**
 * The context sent with the post: the version, the system ("Web · Chrome 141 · Windows 10/11 x64" or "Desktop app ·
 * macOS arm64") and the screen. Plain text, at most 60 characters each.
 */
export function feedbackContext(f: { version: string; desktop: boolean; os: string; browser: string; page?: string }): FeedbackContext {
  const platform = f.desktop ? `Desktop app · ${f.os}` : `Web · ${f.browser} · ${f.os}`;
  const page = f.page ? cutChars(plain(f.page), CONTEXT_MAX) : "";
  return {
    appVersion: cutChars(plain(f.version) || "unknown", CONTEXT_MAX),
    platform: cutChars(plain(platform), CONTEXT_MAX),
    ...(page ? { page } : {}),
  };
}

/** The line the dialog shows: "MC Planner 0.1.2 · Web · Chrome 141 · Windows 10/11 x64 · Planner" */
export function contextLine(c: FeedbackContext): string {
  return [`MC Planner ${c.appVersion}`, c.platform, c.page].filter(Boolean).join(" · ");
}

/**
 * The body of POST /api/v1/feedback/posts. `nonce` only without an account; `context` only the version, the system and
 * the screen, whatever else the caller has.
 */
export function feedbackRequestBody(p: { title: string; body: string; token: string; nonce?: string | null; context: FeedbackContext }): Record<string, unknown> {
  const c = p.context;
  const context: Record<string, string> = {};
  for (const key of ["appVersion", "platform", "page"] as const) {
    const v = typeof c[key] === "string" ? cutChars(plain(c[key] as string), CONTEXT_MAX) : "";
    if (v) context[key] = v;
  }
  return {
    app: FEEDBACK_APP,
    title: p.title.trim(),
    body: p.body.trim(),
    token: p.token,
    ...(p.nonce ? { nonce: p.nonce } : {}),
    context,
  };
}

// ---------------------------------------------------------------- answers

/**
 * What a refusal means for the dialog. The person always sees the account site's own message; the kind only decides
 * what the dialog offers next (a new form, sign-in, sending without an account, the board).
 */
export type ProblemKind = "offline" | "expired" | "too_fast" | "anonymous_off" | "unauthorized" | "email_unconfirmed" | "no_board" | "error";
export interface Problem { kind: ProblemKind; code: string; status: number; message: string }

const KIND_OF: Record<string, ProblemKind> = {
  form_expired: "expired",
  form_too_fast: "too_fast",
  feedback_anonymous_off: "anonymous_off",
  unauthorized: "unauthorized",
  email_unconfirmed: "email_unconfirmed",
  not_found: "no_board",
};

const FALLBACK: Record<ProblemKind, string> = {
  offline: "Couldn't reach Lab43. Check your connection, then press Send again.",
  expired: "This form has expired. Please try again.",
  too_fast: "That was quick. Please wait a moment, then send it again.",
  anonymous_off: "Sending without an account is switched off right now.",
  unauthorized: "Your sign-in has run out. Sign in again, or send it without an account.",
  email_unconfirmed: "Confirm your account's email address first.",
  no_board: "MC Planner's feedback board isn't taking posts right now.",
  error: "",
};

/** A refusal from auth.lab43.dev: `{ error, message }` with any status (0: it never got there). */
export function feedbackProblem(status: number, data: unknown): Problem {
  if (status === 0) return { kind: "offline", code: "offline", status: 0, message: FALLBACK.offline };
  const d = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
  const code = typeof d.error === "string" ? d.error.slice(0, 60) : "";
  const kind: ProblemKind = KIND_OF[code] ?? (!code && status === 401 ? "unauthorized" : !code && status === 404 ? "no_board" : "error");
  const said = typeof d.message === "string" ? cutChars(plain(d.message), MAX_MESSAGE) : "";
  const message = said || FALLBACK[kind] || `Lab43 couldn't take it right now (error ${status}). Please try again later.`;
  return { kind, code: code || `http_${status}`, status, message };
}

export type FormResult = { ok: true; form: FeedbackForm } | { ok: false; problem: Problem };
export type SendResult = { ok: true; visibility: "pending" | "shown"; url: string | null } | { ok: false; problem: Problem };

type Fetch = typeof fetch;
const REQUEST: RequestInit = { credentials: "omit", mode: "cors", cache: "no-store", referrerPolicy: "no-referrer" };

async function call(url: string, init: RequestInit, opts: { fetch?: Fetch; signal?: AbortSignal; timeoutMs?: number }): Promise<{ status: number; data: unknown }> {
  const doFetch = opts.fetch ?? fetch;
  const ctrl = typeof AbortController === "function" ? new AbortController() : null;
  const stop = () => ctrl?.abort();
  opts.signal?.addEventListener("abort", stop);
  const timer = ctrl ? setTimeout(stop, opts.timeoutMs ?? 20_000) : null;
  try {
    const res = await doFetch(url, { ...REQUEST, ...init, signal: ctrl?.signal });
    let data: unknown = null;
    try { data = await res.json(); } catch { /* not JSON */ }
    return { status: res.status, data };
  } catch {
    return { status: 0, data: null };
  } finally {
    if (timer) clearTimeout(timer);
    opts.signal?.removeEventListener("abort", stop);
  }
}

/** GET a new form (every time the dialog opens, and after a form was used up or ran out). Sends nothing about anyone. */
export async function fetchFeedbackForm(opts: { fetch?: Fetch; signal?: AbortSignal; timeoutMs?: number } = {}): Promise<FormResult> {
  const { status, data } = await call(FEEDBACK_FORM_URL, { method: "GET" }, opts);
  if (status === 200) {
    const form = parseFeedbackForm(data);
    if (form) return { ok: true, form };
    return { ok: false, problem: { kind: "error", code: "bad_form", status, message: "Lab43's feedback form didn't load properly. Please try again later." } };
  }
  return { ok: false, problem: feedbackProblem(status, data) };
}

/**
 * POST the post, once: no retries (the person presses Send again). With the person's Lab43 access token it's posted as
 * them; without one it needs the form's proof-of-work in the body. No cookies, no referrer.
 */
export async function sendFeedbackPost(body: Record<string, unknown>, opts: { accessToken?: string | null; fetch?: Fetch; timeoutMs?: number } = {}): Promise<SendResult> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (opts.accessToken) headers.Authorization = `Bearer ${opts.accessToken}`;
  const { status, data } = await call(FEEDBACK_POSTS_URL, { method: "POST", headers, body: JSON.stringify(body) }, opts);
  if (status === 200 || status === 201) {
    const post = (data && typeof data === "object" ? (data as { post?: unknown }).post : null) as { visibility?: unknown; url?: unknown } | null;
    const visibility = post?.visibility === "shown" ? "shown" : "pending";
    return { ok: true, visibility, url: visibility === "shown" ? boardLink(post?.url) : null };
  }
  return { ok: false, problem: feedbackProblem(status, data) };
}
